export interface ApiResponse<T = unknown> {
  ok: boolean;
  status: number;
  data: T;
  error?: string;
}

export interface ApiOk<T> {
  ok: true;
  status: number;
  data: T;
}

export interface ApiErr {
  ok: false;
  status: number;
  data: unknown;
  error?: string;
  fieldErrors?: Record<string, string>;
}

export type ApiResult<T> = ApiOk<T> | ApiErr;

function extractErrors(body: unknown, fallback: string): { error?: string; fieldErrors?: Record<string, string> } {
  if (body && typeof body === 'object' && !Array.isArray(body)) {
    const o = body as Record<string, unknown>;
    if (typeof o.error === 'string') return { error: o.error };
    if (typeof o.message === 'string') return { error: o.message };

    const fieldErrors: Record<string, string> = {};
    for (const [k, v] of Object.entries(o)) {
      if (typeof v === 'string') fieldErrors[k] = v;
      else if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'string') fieldErrors[k] = v[0];
    }
    if (Object.keys(fieldErrors).length > 0) return { fieldErrors };
  }
  return { error: typeof body === 'string' ? body : fallback };
}

export async function readJsonResponse<T>(res: Response): Promise<ApiResult<T>> {
  const text = await res.text();
  let parsed: unknown = text;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = text;
  }

  if (!res.ok) {
    const err = extractErrors(parsed, res.statusText);
    return { ok: false, status: res.status, data: parsed, ...err };
  }

  return { ok: true, status: res.status, data: parsed as T };
}

export async function readJsonApiResponse<T>(
  res: Response,
  fallbackError: string,
  emptyData: T
): Promise<ApiResponse<T>> {
  const result = await readJsonResponse<T>(res);
  if (!result.ok) {
    return {
      ok: false,
      status: result.status,
      data: (result.data as T) ?? emptyData,
      error: result.error || fallbackError
    };
  }
  return {
    ok: true,
    status: result.status,
    data: result.data
  };
}
